import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import axiosInstance from 'axiosConfig';
import { Toast } from 'components';
import { TOAST_TYPES_ENUM } from 'enums';
import { queryClient } from 'App';
import { usePWA } from 'hooks/usePWA';
import { getActivityTimerSession, clearActivityTimerSession, getNowFormatted } from 'hooks/useOfflineActivityTimer';
import { mutateWithOffline } from 'utils/mutateWithOffline';

type ActivityTimeStopData = {
  timeManagementUuid: string;
};

const usePutActivityTimeStop = (
  options?: UseMutationOptions<any, Error, ActivityTimeStopData>
) => {
  const { isOffline } = usePWA();

  return useMutation({
    ...options,
    mutationKey: ['stopActivityTime'],
    mutationFn: async (data: ActivityTimeStopData) => {
      // ONLINE – zachowanie jak dotychczas
      if (!isOffline) {
        return axiosInstance.put(`/activity/time-stop`, data).then((res) => res.data);
      }

      // OFFLINE – zamiana na POST /activity/time na podstawie zapisanej sesji
      const session = getActivityTimerSession();

      if (!session) {
        console.warn('⚠️ No offline activity timer session found when trying to stop activity time offline');
        // Zwróć bezpieczną odpowiedź, żeby UI się nie rozjechał
        return {
          status: 'error',
          message: 'Brak zapisanej sesji czasu do zatrzymania w trybie offline.',
        };
      }

      const dateEnd = getNowFormatted();

      console.log(session,'session poka co tam jest kurka')

      const body = {
        activityUuid: session.activityUuid,
        orderUuid: session.orderUuid,
        comment: session.comment ?? undefined,
        activityName: session.activityName ?? undefined,
        dateStart: session.dateStart,
        userSymbols: session.userSymbols,
        dateEnd,
      };

      console.log('⏱️ [usePutActivityTimeStop] Offline stop → queue POST /activity/time', body);

      const result = await mutateWithOffline(
        'POST',
        '/activity/time',
        body,
        {
          entityType: 'activityTime',
          forceOffline: true,
          simulateResponse: () => {
            const tempUuid = `offline-time-${Date.now()}`;
            return {
              status: 'success',
              message: 'Time created (offline)',
              timeManagement: {
                uuid: tempUuid,
                dateStart: session.dateStart,
                dateEnd,
                countingTime: false,
                countedTime: '',
                comment: body.comment ?? null,
                activityName: body.activityName ?? null,
                firstDate: session.dateStart,
                userSymbols: session.userSymbols,
              },
            };
          },
        }
      );

      console.log('⏱️ [usePutActivityTimeStop] Offline stop mutateWithOffline result:', result);

      // Zaktualizuj lokalny cache time-management, aby nowy wpis był widoczny w offline
      try {
        console.log('📦 [usePutActivityTimeStop] Updating offlineApiCache for time-management...');
        const cacheKey = `/activity/time-management?orderUuid=${session.orderUuid}`;
        const raw = localStorage.getItem('offlineApiCache');

        if (raw) {
          const cache = JSON.parse(raw) as Record<string, { data: any; timestamp: number; url: string }>;
          const entry = cache[cacheKey];

          console.log('📦 [usePutActivityTimeStop] Existing cache entry for key:', cacheKey, entry);

          if (entry && entry.data && Array.isArray(entry.data.activityMeasurementLabel)) {
            const list = entry.data.activityMeasurementLabel;

            console.log('📦 [usePutActivityTimeStop] activityMeasurementLabel length:', list.length);

            const target = list.find((a: any) => {
              if (session.activityUuid && a.uuid === session.activityUuid) return true;
              if (session.activityName && a.activities === session.activityName) return true;
              return false;
            });

            console.log('🎯 [usePutActivityTimeStop] Matched activity item for offline time:', target);

            if (target) {
              const newTime = (result as any).timeManagement;

              console.log('🕒 [usePutActivityTimeStop] New offline timeManagement to append:', newTime);
              const existing = Array.isArray(target.timeManagement)
                ? target.timeManagement
                : [];

              // USUWAMY tymczasowy otwarty wpis (offline-time-open-...), żeby nie było duplikatu
              const filteredExisting = existing.filter(
                (t: any) => !t.uuid?.startsWith('offline-time-open-')
              );

              target.timeManagement = [
                {
                  ...newTime,
                  userSymbols: target.userSymbols ?? newTime.userSymbols ?? '',
                },
                ...filteredExisting,
              ];

              // Ustaw countingTime: false na poziomie aktywności (jak w wersji online)
              target.countingTime = false;

              const parseToMinutes = (time: string | null | undefined) => {
                if (!time) return 0;
                const parts = String(time).split(':');
                if (parts.length !== 2) return 0;
                const [h, m] = parts.map((p) => Number(p));
                if (Number.isNaN(h) || Number.isNaN(m)) return 0;
                return h * 60 + m;
              };

              const totalMinutes = target.timeManagement.reduce(
                (sum: number, t: any) => sum + parseToMinutes(t.countedTime),
                0
              );

              const hours = Math.floor(totalMinutes / 60);
              const minutes = totalMinutes % 60;
              target.allTime = `${hours}:${minutes.toString().padStart(2, '0')}`;

              console.log('🧮 [usePutActivityTimeStop] Recomputed allTime for activity:', target.allTime);
            }

            cache[cacheKey] = {
              ...entry,
              data: {
                ...entry.data,
                activityMeasurementLabel: list,
              },
              timestamp: Date.now(),
            };

            localStorage.setItem('offlineApiCache', JSON.stringify(cache));
            console.log('💾 [usePutActivityTimeStop] offlineApiCache updated for key:', cacheKey);
          }
        }
      } catch (error) {
        console.error('❌ Error updating offline time-management cache after offline stop:', error);
      }

      // Poproś service workera o aktualizację API_CACHE dla time-management (jeśli jest aktywny)
      try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          const offlineTime = (result as any).timeManagement;
          const baseUrl = (axiosInstance.defaults.baseURL || '').replace(/\/+$/, '');
          const apiUrl = `${baseUrl}/activity/time-management?orderUuid=${session.orderUuid}`;

          navigator.serviceWorker.controller.postMessage({
            type: 'UPDATE_ACTIVITY_TIME_MANAGEMENT',
            orderUuid: session.orderUuid,
            activityUuid: session.activityUuid,
            activityName: session.activityName,
            timeManagement: offlineTime,
            apiUrl,
          });
          console.log('📨 [usePutActivityTimeStop] Sent UPDATE_ACTIVITY_TIME_MANAGEMENT to SW', {
            orderUuid: session.orderUuid,
            activityUuid: session.activityUuid,
            activityName: session.activityName,
            timeManagement: offlineTime,
            apiUrl,
          });
        } else {
          console.log('⚠️ [usePutActivityTimeStop] No active service worker controller, skipping SW cache update');
        }
      } catch (error) {
        console.error('❌ Error sending UPDATE_ACTIVITY_TIME_MANAGEMENT message to SW:', error);
      }

      // Po wygenerowaniu czasu offline wyczyść sesję timera
      clearActivityTimerSession();

      return result;
    },
    onMutate: (variables) => {
      Toast({
        message: 'Zatrzymywanie liczenia czasu dla aktywności...',
        type: TOAST_TYPES_ENUM.LOADING,
        id: 'stop-activity-time',
      });
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      Toast({
        message: isOffline
          ? 'Czas dla aktywności zostanie zapisany po synchronizacji.'
          : 'Czas dla aktywności zatrzymany pomyślnie.',
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: 'stop-activity-time',
      });
      queryClient.invalidateQueries({ queryKey: ['timeManagement'] });
      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      Toast({
        message: 'Błąd podczas zatrzymywania mierzenia czasu dla aktywności.',
        type: TOAST_TYPES_ENUM.ERROR,
        id: 'stop-activity-time',
      });
      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

export default usePutActivityTimeStop;
